import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, MapPin, FileText, CheckCircle, HelpCircle } from 'lucide-react';
import { useToast } from '../contexts/ToastContext';
import { isAuthenticated, getAssociateData } from '../services/otpAuthService';
import { useTour } from '../hooks/useTour';
import GuidedTour from '../components/GuidedTour';

// Step Components (da creare)
import StepRicercaMappa from '../components/svincolo-atti/StepRicercaMappa';
import StepDatiAtto from '../components/svincolo-atti/StepDatiAtto';
import StepRiepilogo from '../components/svincolo-atti/StepRiepilogo';

interface StudioSelezionato {
  _id: string;
  ragioneSociale: string;
  citta: string;
  provincia: string;
  indirizzo: string;
  civico: string;
  telefono?: string;
  email: string;
  parentMemberId?: string;
  isSecondarySede?: boolean;
  distanza?: number;
}

interface DatiAtto {
  targa: string;
  prezzoVendita: number;
  soggettoIVA: boolean;
  tipoAtto: 'art56' | 'normale';
  dataAppuntamento: string;
  oraAppuntamento: string;
  modalitaPagamento: 'venditore' | 'bonifico';
  emailVenditore: string;
}

const SvincoloAttiPage: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const [currentStep, setCurrentStep] = useState(1);
  const [studiSelezionati, setStudiSelezionati] = useState<StudioSelezionato[]>([]);
  const [datiAtto, setDatiAtto] = useState<DatiAtto | null>(null);

  // Guided tour
  const { runTour, tourKey, startTour, handleCallback } = useTour('svincolo_workflow_tour');

  const tourSteps = [
    {
      target: '#tour-svincolo-steps',
      content: 'Questo è il percorso in 3 passaggi per creare una nuova richiesta di svincolo.',
      skipBeacon: true,
      placement: 'bottom' as const
    },
    {
      target: '#tour-svincolo-info',
      content: 'Leggi qui come funziona il processo di svincolo tra studi.',
      placement: 'top' as const
    },
    {
      target: '#tour-svincolo-content',
      content: 'Inizia cercando lo studio a cui inviare la richiesta, puoi cercare per indirizzo sulla mappa.',
      placement: 'top' as const
    }
  ];

  // Scroll to top quando cambia lo step
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentStep]);

  useEffect(() => {
    // Verifica autenticazione
    if (!isAuthenticated()) {
      toast.error('Devi effettuare l\'accesso');
      navigate('/accesso-associati');
      return;
    }

    // Verifica se l'associato è nella rete certificata
    const associate = getAssociateData();
    if (!associate) {
      toast.error('Dati associato non trovati');
      navigate('/accesso-associati');
      return;
    }

    // TODO: Verificare se l'associato è nella rete certificata
    // Per ora assumiamo che sia verificato lato backend
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intenzionale: dipendenze ricreate a ogni render, aggiungerle cambierebbe la frequenza di esecuzione
  }, [navigate]);

  const steps = [
    { number: 1, title: 'Ricerca Studio', icon: MapPin, description: 'Cerca colleghi sulla mappa' },
    { number: 2, title: 'Dati Atto', icon: FileText, description: 'Inserisci i dettagli' },
    { number: 3, title: 'Riepilogo', icon: CheckCircle, description: 'Conferma e invia' }
  ];

  const handleStepComplete = (step: number, data: any) => {
    if (step === 1) {
      setStudiSelezionati(data);
      setCurrentStep(2);
    } else if (step === 2) {
      setDatiAtto(data);
      setCurrentStep(3);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    } else {
      navigate('/area-riservata');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-purple-50/30 to-gray-50 dark:from-gray-950 dark:via-purple-950/20 dark:to-gray-950">
      <GuidedTour steps={tourSteps} run={runTour} tourKey={tourKey} onCallback={handleCallback} />
      {/* Header */}
      <div className="bg-white/80 dark:bg-gray-900/80 backdrop-blur-sm border-b border-gray-200/50 dark:border-gray-800/50 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={handleBack}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-gray-600 dark:text-gray-400" />
              </button>
              <div>
                <h1 className="text-xl font-bold text-gray-900 dark:text-white">Svincolo atti di vendita</h1>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Richiedi assistenza per la firma dell'atto
                </p>
              </div>
            </div>
            <button
              onClick={startTour}
              className="p-2 rounded-lg text-gray-400 hover:text-purple-500 hover:bg-purple-50 dark:hover:bg-purple-900/10 transition-colors"
              title="Guida"
            >
              <HelpCircle className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Progress Steps */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div id="tour-svincolo-steps" className="mb-8">
          <div className="flex items-center justify-between">
            {steps.map((step, index) => {
              const Icon = step.icon;
              const isActive = currentStep === step.number;
              const isCompleted = currentStep > step.number;

              return (
                <React.Fragment key={step.number}>
                  <div className="flex flex-col items-center flex-1">
                    <div
                      className={`w-12 h-12 rounded-full flex items-center justify-center transition-all duration-300 ${
                        isCompleted
                          ? 'bg-green-500 text-white'
                          : isActive
                          ? 'bg-purple-600 text-white shadow-lg shadow-purple-500/50'
                          : 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                      }`}
                    >
                      {isCompleted ? (
                        <CheckCircle className="w-6 h-6" />
                      ) : (
                        <Icon className="w-6 h-6" />
                      )}
                    </div>
                    <div className="mt-2 text-center">
                      <p
                        className={`text-sm font-semibold ${
                          isActive
                            ? 'text-purple-600 dark:text-purple-400'
                            : isCompleted
                            ? 'text-green-600 dark:text-green-400'
                            : 'text-gray-500 dark:text-gray-400'
                        }`}
                      >
                        {step.title}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 hidden sm:block">
                        {step.description}
                      </p>
                    </div>
                  </div>

                  {index < steps.length - 1 && (
                    <div
                      className={`flex-1 h-1 mx-4 rounded transition-all duration-300 ${
                        currentStep > step.number
                          ? 'bg-green-500'
                          : 'bg-gray-200 dark:bg-gray-700'
                      }`}
                    />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>

        {/* Step Content */}
        <div id="tour-svincolo-content" className="bg-white dark:bg-gray-900 rounded-xl shadow-lg border border-gray-200 dark:border-gray-800 p-6">
          {currentStep === 1 && (
            <StepRicercaMappa
              onComplete={(studi) => handleStepComplete(1, studi)}
              initialSelection={studiSelezionati}
            />
          )}

          {currentStep === 2 && (
            <StepDatiAtto
              studiSelezionati={studiSelezionati}
              onComplete={(dati) => handleStepComplete(2, dati)}
              onBack={handleBack}
              initialData={datiAtto}
            />
          )}

          {currentStep === 3 && (
            <StepRiepilogo
              studiSelezionati={studiSelezionati}
              datiAtto={datiAtto!}
              onBack={handleBack}
              onSuccess={() => {
                toast.success('Richiesta inviata con successo!');
                navigate('/area-riservata/svincolo-atti/lista');
              }}
            />
          )}
        </div>

        {/* Info Box */}
        <div id="tour-svincolo-info" className="mt-6 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-blue-900 dark:text-blue-100 mb-2">
            ℹ️ Come funziona
          </h3>
          <ul className="text-sm text-blue-800 dark:text-blue-200 space-y-1">
            <li>1. Cerca sulla mappa i colleghi vicini all'indirizzo del venditore</li>
            <li>2. Seleziona uno o più studi e compila i dati dell'atto</li>
            <li>3. Gli studi selezionati riceveranno una notifica e potranno inviare un preventivo</li>
            <li>4. Scegli il preventivo migliore e carica i documenti necessari</li>
            <li>5. Il venditore riceverà i dettagli dell'appuntamento via email</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default SvincoloAttiPage;

